import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Footer from '../components/Footer';
import '../style.css';
import '../nov.css';

const Resources = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Auto slide effect - her 4 saniyede bir tab değişir
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % resources.length);
    }, 4000); // 4 saniyede bir değişir

    return () => clearInterval(timer);
  }, []);

  const resources = [
    {
      title: "Educational Materials",
      description: "Dive deep into the essentials of copyright, digital distribution, and music marketing.",
      detailedDescription: "In the dynamic landscape of the music industry, a deep understanding of copyright, digital distribution, and music marketing is crucial for success. We provide comprehensive guides covering these essential topics, empowering you to navigate the complexities of the business. Learn how to protect your copyrights, effectively distribute your music, and leverage music marketing strategies to grow your fanbase.",
      buttonColor: "text-[#9BCFFF]"
    },
    {
      title: "Legal Documents",
      description: "Access a library of essential contracts, forms, and guides for legal clarity.",
      detailedDescription: "We provide a variety of contract templates, copyright registration forms, and legal guides tailored to address various legal scenarios you might encounter in your music career. These resources will help you understand your rights, draft agreements correctly, and ensure that your music is legally protected.",
      buttonColor: "text-[#FCD4FE]"
    },
    {
      title: "Industry Trends",
      description: "Stay ahead of the curve with our insights into the latest music industry trends.",
      detailedDescription: "Staying ahead of the curve and understanding the latest trends is critical for thriving in the ever-evolving music industry. This section provides in-depth analysis of current market trends, emerging technologies, and predictions for the future. Stay informed about the forces shaping the music landscape and empower yourself to make informed decisions.",
      buttonColor: "text-black"
    },
    {
      title: "Music Production Tips",
      description: "Elevate your sound with expert insights on studio techniques and production hacks.",
      detailedDescription: "Want to learn the fundamentals of music production? We provide in-depth information on recording studio techniques, audio engineering principles, and professional production hacks. These tips will help you enhance your sound quality, unlock your creative potential, and achieve a professional sound in your tracks.",
      buttonColor: "text-[#ffaa40]"
    },
    {
      title: "Networking & Collaborations",
      description: "Expand your network and find collaborators. We connect you with industry professionals.",
      detailedDescription: "Success in the music industry is not just about talent; it's about building a strong network and forging strategic collaborations. This section provides guidance on how to connect with industry professionals, establish mutually beneficial partnerships, and leverage your network effectively to advance your music career.",
      buttonColor: "text-[#a855f7]"
    },
    {
      title: "Financial Planning",
      description: "Plan for a secure future in music. Learn about budgeting and smart investments.",
      detailedDescription: "Solid financial planning is essential to making your music career sustainable. This section provides practical insights on managing your music income, budgeting effectively, making smart investments, and developing strategies for long-term financial success.",
      buttonColor: "text-[#3b82f6]"
    }
  ];

  return (
    <>
    <section id="resources" className="min-h-screen bg-[#f5f5f7] py-32 lg:py-48 relative overflow-hidden" style={{ fontFamily: 'aesthet-nova, Helvetica, Arial' }}>
      <div className="container mx-auto px-6 lg:px-16 max-w-[1400px] relative z-10">
        <div className="w-full flex flex-col lg:flex-row items-center lg:items-start justify-between gap-16 lg:gap-24">
          
          {/* Left Side - Content */}
          <div className="lg:w-5/12 text-center lg:text-left space-y-10">
            <motion.div
              className="inline-block"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <span 
                className="text-xs font-medium badge-gradient text-gray-700 py-2.5 px-5 rounded-full inline-block uppercase tracking-wide" 
                style={{ fontFamily: 'aesthet-nova' }}
              >
                RESOURCES
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight uppercase" style={{ fontFamily: 'aesthet-nova' }}>
                <span style={{ color: 'rgb(70, 52, 135)' }}>
                  FUEL YOUR
                </span>
                <br/>
                <span 
                  style={{ '--shimmer-width': '250px' }} 
                  className="text-neutral-600/50 animate-shimmer bg-clip-text bg-no-repeat [background-position:0_0] [background-size:var(--shimmer-width)_100%] [transition:background-position_1s_cubic-bezier(.6,.6,0,1)_infinite] bg-gradient-to-r from-neutral-100 via-black/80 via-50% to-neutral-100"
                >
                  MUSIC JOURNEY
                </span>
                <br/>
                <span style={{ color: 'rgb(70, 52, 135)' }}>
                  WITH INSIGHTS.
                </span>
              </h1>
            </motion.div>

            <motion.p 
              className="text-gray-600 text-lg leading-relaxed" 
              style={{ fontFamily: 'aesthet-nova' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Media Nov is a leading platform dedicated to empowering independent artists and revolutionizing the music industry. We provide you with a wealth of resources to navigate the complexities of the business, enhance your skills, and achieve your musical aspirations.
            </motion.p>
          </div>

          {/* Right Side - Tab List */}
          <div className="lg:w-7/12 flex flex-col gap-6">
            {/* Tab Buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {resources.map((resource, index) => (
                <motion.button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`relative bg-white rounded-[24px] px-4 py-4 transition-all duration-200 card-shimmer text-left ${
                    activeTab === index ? 'ring-2 ring-purple-500' : ''
                  }`}
                  style={{ filter: 'blur(0px)' }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <h3 className="text-sm font-bold uppercase" style={{ fontFamily: 'aesthet-nova' }}>
                    <span className="bg-gradient-to-r from-[#9BCFFF] via-[#FCD4FE] to-[#B6F1F6] text-transparent bg-clip-text">
                      {resource.title}
                    </span>
                  </h3>
                </motion.button>
              ))}
            </div>

            {/* Tab Content */}
            <motion.div
              key={activeTab}
              className="bg-white rounded-[24px] p-8 card-shimmer relative"
              style={{ filter: 'blur(0px)' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl font-bold uppercase mb-4" style={{ fontFamily: 'aesthet-nova' }}>
                <span className="bg-gradient-to-r from-[#9BCFFF] via-[#FCD4FE] to-[#B6F1F6] text-transparent bg-clip-text">
                  {resources[activeTab].title}
                </span>
              </h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4" style={{ fontFamily: 'aesthet-nova' }}>
                {resources[activeTab].description}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: 'aesthet-nova' }}>
                {resources[activeTab].detailedDescription}
              </p>

              {/* Progress Indicators */}
              <div className="flex gap-2 mt-6 justify-center">
                {resources.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTab(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === activeTab 
                        ? 'w-8 bg-gradient-to-r from-[#9BCFFF] via-[#FCD4FE] to-[#B6F1F6]' 
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Tab ${index + 1}`}
                  />
                ))}
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>

    {/* Footer */}
    <Footer />
    </>
  );
};

export default Resources;
